 // src/components/MegaMenu.jsx

import { useRef, useState, useEffect } from "react";
import {
  CaretDownOutlined,
  CaretRightOutlined,
  MenuOutlined,
} from "@ant-design/icons";

import { Tabs, Drawer, Spin } from "antd";

import { ReactComponent as TicketOutline } from "../assets/images/menu-ticket.svg";
import { ReactComponent as TransportationIcon } from "../assets/images/menu-transport.svg";
import { ReactComponent as TourOutline } from "../assets/images/menu-tour.svg";
import { ReactComponent as TravelService } from "../assets/images/menu-travel-services.svg";
import { ReactComponent as CruiseOutline } from "../assets/images/menu-cruise.svg";
import { ReactComponent as FoodDrinks } from "../assets/images/menu-food.svg";
import { ReactComponent as EntertainmentIcon } from "../assets/images/menu-entreatment.svg";
import { ReactComponent as AdventureIcon } from "../assets/images/menu-adventure.svg";

import { useNavigate } from "react-router-dom";

import axiosInstance from "../api/axiosInstance";

// ✅ Export icons map
export const ICON_MAP = {
  tickets: <TicketOutline />,
  tour: <TourOutline />,
  transportation: <TransportationIcon />,
  travelservices: <TravelService />,
  cruises: <CruiseOutline />,
  food: <FoodDrinks />,
  entertainment: <EntertainmentIcon />,
  adventure: <AdventureIcon />,
};

// ✅ Export tab generator
export const getTabItems = (
  products = [],
  navigate,
  setOpen
) => {
  if (!Array.isArray(products)) products = [];

  return products.map((product) => {
    const visibleSubProducts = (
      product.sub_products || []
    ).filter((sub) => !sub.is_hidden);

    return {
      key: product.slug || product.id,

      label: (
        <span className="flex items-center gap-2">
          {ICON_MAP[product.slug] || (
            <AdventureIcon />
          )}
          {product.name || "Unnamed"}
        </span>
      ),

      children: (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 p-4 md:px-6 md:py-10">
          {visibleSubProducts.length > 0 ? (
            visibleSubProducts.map((sub) => (
              <div
                key={sub.id}
                onClick={() => {
                  navigate(
                    `/activity/${product.slug}/${sub.slug}`
                  );

                  setOpen(false);
                }}
                className="flex items-center text-gray-700 font-medium hover:text-blue-600 cursor-pointer"
              >
                <CaretRightOutlined className="text-gray-300 mr-2" />

                {sub.title || "Untitled"}
              </div>
            ))
          ) : (
            <div className="text-gray-400 italic">
              No subcategories available
            </div>
          )}
        </div>
      ),
    };
  });
};

const MegaMenu = ({ setMobileTabItems }) => {
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);

  const [drawerOpen, setDrawerOpen] =
    useState(false);

  const navigate = useNavigate();

  const dropdownRef = useRef(null);

  // ✅ Fetch Products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axiosInstance.get(
          "/products?populate=sub_products"
        );

 

        // ✅ safer response handling
        let data = [];

        if (Array.isArray(res?.data)) {
          data = res.data;
        } else if (
          Array.isArray(res?.data?.data)
        ) {
          data = res.data.data;
        } else if (
          Array.isArray(res?.data?.products)
        ) {
          data = res.data.products;
        }

    

        setProducts(data);

        // optional mobile tabs
        if (setMobileTabItems) {
          setMobileTabItems(
            getTabItems(
              data,
              navigate,
              setDrawerOpen
            )
          );
        }
      } catch (error) {
        console.error(
          "Error fetching products:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [navigate, setMobileTabItems]);

  // ✅ Outside click close
  useEffect(() => {
    const handleClickOutside = (
      event
    ) => {
      if (!dropdownRef.current) return;

      if (
        !dropdownRef.current.contains(
          event.target
        )
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "click",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "click",
        handleClickOutside
      );
    };
  }, []);

  const tabItems = getTabItems(
    products,
    navigate,
    setOpen
  );

  if (loading) {
    return (
      <div className="hidden md:block p-4">
        <Spin />
      </div>
    );
  }

  return (
    <div
      ref={dropdownRef}
      className="relative"
    >
      {/* Desktop Toggle */}
      <div className="hidden md:flex items-center cursor-pointer font-semibold text-gray-800">
        <span
          onClick={() =>
            navigate("/activity")
          }
        >
          All Categories
        </span>

        <CaretDownOutlined
          onClick={() =>
            setOpen((prev) => !prev)
          }
          style={{
            transition:
              "transform 0.3s ease",
            transform: open
              ? "rotate(180deg)"
              : "rotate(0deg)",
          }}
          className="ml-2 w-10 h-6 rounded shadow flex items-center justify-center cursor-pointer"
        />
      </div>

      {/* Mobile Menu */}
      <div
        className="flex md:hidden items-center cursor-pointer text-gray-800"
        onClick={() =>
          setDrawerOpen(true)
        }
      >
        <MenuOutlined className="text-xl" />

        <span className="ml-2">
          Menu
        </span>
      </div>

      {/* Desktop Mega Menu */}
      {open && (
        <div className="absolute top-12 w-[calc(100vw-6vw)] bg-white shadow-lg z-50 rounded-b-xl overflow-hidden">
         <div className="">
          <Tabs
            defaultActiveKey={
              tabItems[0]?.key
            }
            items={tabItems}
            tabPosition="left"
            className="
            w-full
              mainMenu
              activeColor

              [&_.ant-tabs]:!w-full
              [&_.ant-tabs-content-holder]:!w-full
              [&_.ant-tabs-nav]:!min-w-[260px]

              [&_.ant-tabs-tab]:!px-5
              [&_.ant-tabs-tab]:!py-6
              [&_.ant-tabs-tab]:!text-gray-700

              [&_.ant-tabs-tab:hover]:!bg-primary
              [&_.ant-tabs-tab:hover]:!text-white

              [&_.ant-tabs-tab-active]:!bg-primary
              [&_.ant-tabs-tab-active]:!text-white
            "
          />
          </div>
        </div>
      )}

      
    </div>
  );
};

export default MegaMenu;